import { gql } from 'graphql-request';
import { ORGANIZATION_SUBSCRIPTION_FIELDS } from './subscription.queries';

export const ORGANIZATION_FIELDS = gql`
  fragment OrganizationFields on OrganizationDto {
    organizationId
    name
    status
    ownerEmail
    ownersEmails
    ownersNames
    showAsSubcontractor
    atavEnabled
    invoicingPaused
    completenessGateMode
    dailyReportTimezone
    dailyReportSendHour
    subscriptionId
    subscriptionStatus
    planName
    planId
    trialEndsAt
    trialExpiredAt
    billingPeriod
    hasNmiSubscription
    hasPaymentMethod
  }
`;

export const ORGANIZATION_WITH_SUBSCRIPTION_FIELDS = gql`
  fragment OrganizationWithSubscriptionFields on OrganizationWithSubscriptionDto {
    organization {
      ...OrganizationFields
    }
    subscription {
      ...OrganizationSubscriptionFields
    }
  }
  ${ORGANIZATION_FIELDS}
  ${ORGANIZATION_SUBSCRIPTION_FIELDS}
`;

export const getOrganizationByID = gql`
  query getOrganizationByID($id: String!) {
    getOrganizationByID(id: $id) {
      ...OrganizationFields
    }
  }
  ${ORGANIZATION_FIELDS}
`;

export const getOrganizations = gql`
  query getOrganizations($organizations: GetOrganizationsInput!) {
    getOrganizations(organizations: $organizations) {
      organizations {
        ...OrganizationFields
      }
      count
    }
  }
  ${ORGANIZATION_FIELDS}
`;

export const getOrganizationsForTaskCreation = gql`
  query getOrganizationsForTaskCreation(
    $organizations: GetOrganizationsInput!
  ) {
    getOrganizationsForTaskCreation(organizations: $organizations) {
      organizations {
        ...OrganizationFields
      }
      count
    }
  }
  ${ORGANIZATION_FIELDS}
`;

export const getUserOrganizations = gql`
  query getUserOrganizations {
    getUserOrganizations {
      ...OrganizationFields
    }
  }
  ${ORGANIZATION_FIELDS}
`;

export const getSubcontractors = gql`
  query getSubcontractors {
    getSubcontractors {
      organizationId
      name
      status
      showAsSubcontractor
      projects {
        projectId
        name
        address
        startDate
        endDate
        budget
        state
        originalProjectId
      }
    }
  }
`;

export const createOrganization = gql`
  mutation createOrganization($organization: OrganizationCreateInput!) {
    createOrganization(organization: $organization) {
      ...OrganizationFields
    }
  }
  ${ORGANIZATION_FIELDS}
`;

export const updateOrganization = gql`
  mutation updateOrganization($organization: OrganizationUpdateInput!) {
    updateOrganization(organization: $organization) {
      ...OrganizationFields
    }
  }
  ${ORGANIZATION_FIELDS}
`;

export const disableOrganization = gql`
  mutation disableOrganization($id: String!) {
    disableOrganization(id: $id) {
      ...OrganizationFields
    }
  }
  ${ORGANIZATION_FIELDS}
`;

export const enableOrganization = gql`
  mutation enableOrganization($id: String!) {
    enableOrganization(id: $id) {
      ...OrganizationFields
    }
  }
  ${ORGANIZATION_FIELDS}
`;

export const setOrganizationInvoicingPaused = gql`
  mutation setOrganizationInvoicingPaused($id: String!, $paused: Boolean!) {
    setOrganizationInvoicingPaused(id: $id, paused: $paused) {
      ...OrganizationFields
    }
  }
  ${ORGANIZATION_FIELDS}
`;

export const setOrganizationsInvoicingPaused = gql`
  mutation setOrganizationsInvoicingPaused($ids: [String!]!, $paused: Boolean!) {
    setOrganizationsInvoicingPaused(ids: $ids, paused: $paused)
  }
`;

export const setOrganizationsEnabled = gql`
  mutation setOrganizationsEnabled($ids: [String!]!, $enable: Boolean!) {
    setOrganizationsEnabled(ids: $ids, enable: $enable)
  }
`;

export const getMyOrganizationInvoicingPaused = gql`
  query getMyOrganizationInvoicingPaused {
    getMyOrganizationInvoicingPaused
  }
`;

export const createOrganizationWithPayment = gql`
  mutation createOrganizationWithPayment(
    $input: CreateOrganizationWithPaymentInput!
  ) {
    createOrganizationWithPayment(input: $input) {
      ...OrganizationWithSubscriptionFields
    }
  }
  ${ORGANIZATION_WITH_SUBSCRIPTION_FIELDS}
`;

// Create an additional organization on a card-free trial.
// Capped server-side at MAX_FREE_TRIAL_ORGANIZATIONS non-paying organizations per user.
export const createOrganizationWithTrial = gql`
  mutation createOrganizationWithTrial(
    $input: CreateOrganizationWithTrialInput!
  ) {
    createOrganizationWithTrial(input: $input) {
      ...OrganizationWithSubscriptionFields
    }
  }
  ${ORGANIZATION_WITH_SUBSCRIPTION_FIELDS}
`;

const OrganizationsGraphQL = {
  queries: {
    getOrganizationByID,
    getOrganizations,
    getOrganizationsForTaskCreation,
    getUserOrganizations,
    getSubcontractors,
    getMyOrganizationInvoicingPaused,
  },
  mutations: {
    createOrganization,
    updateOrganization,
    disableOrganization,
    enableOrganization,
    setOrganizationInvoicingPaused,
    setOrganizationsInvoicingPaused,
    setOrganizationsEnabled,
    createOrganizationWithPayment,
    createOrganizationWithTrial,
  },
};

export default OrganizationsGraphQL;
